import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Box, Button, Stack } from '@shiftfinancial/design-system';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const useStyles = makeStyles((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    fontSize: 24,
    [theme.breakpoints.up('lg')]: {
      fontSize: 30,
    },
    '& svg': {
      marginRight: 8,
      [theme.breakpoints.up('lg')]: {
        marginRight: 12,
      },
    },
  },
  subtitleWrapper: {
    paddingBottom: '0 !important',
  },
  subtitle: {
    margin: 0,
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
    },
  },
  topContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5),
    },
  },
  bottomContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5),
    },
    backgroundColor: theme.palette.blue.lighter,
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      width: '100%',
      bottom: 35,
    },
  },
  container: {
    [theme.breakpoints.down('xs')]: {
      minHeight: '78vh',
      paddingBottom: 158,
    },
  },
}));

const StepContainer = ({
  title,
  subtitle,
  submitLabel,
  isSubmitVisible,
  isSubmitDisabled,
  submitButtonAppearance,
  submitButtonAdditionalCssClassName,
  onSubmit,
  children,
  bottomChildren,
  footerLeftChildren,
  ...others
}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width:480px)');

  return (
    <div className={bottomChildren ? classes.container : undefined}>
      <div className={classes.topContent}>
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid item xs={12}>
            <Typography variant="h1" className={classes.title}>
              {others.icon && <others.icon />} {title}
            </Typography>
          </Grid>
          {subtitle && (
            <Grid item xs={12} className={classes.subtitleWrapper}>
              <Typography variant="subtitle1" className={classes.subtitle}>
                {subtitle}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            {children}
          </Grid>
        </Grid>

        <Box asChild sx={{ marginTop: 'x4' }}>
          <Stack
            justifyContent={{ sm: footerLeftChildren ? 'space-between' : 'flex-end' }}
            orientation={{ xs: 'vertical', sm: 'horizontal' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            {footerLeftChildren && <div>{footerLeftChildren}</div>}
            {isSubmitVisible && (
              <Button
                appearance={submitButtonAppearance}
                size="standard"
                className={submitButtonAdditionalCssClassName}
                onClick={onSubmit}
                disabled={isSubmitDisabled}
                data-testid="stepSubmit"
              >
                {submitLabel}
              </Button>
            )}
          </Stack>
        </Box>
      </div>

      {bottomChildren && <div className={classes.bottomContent}>{bottomChildren}</div>}
    </div>
  );
};

StepContainer.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  submitLabel: PropTypes.string,
  isSubmitVisible: PropTypes.bool,
  isSubmitDisabled: PropTypes.bool,
  submitButtonAppearance: PropTypes.string,
  submitButtonAdditionalCssClassName: PropTypes.string,
  onSubmit: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  bottomChildren: PropTypes.node,
  footerLeftChildren: PropTypes.node,
};

StepContainer.defaultProps = {
  title: null,
  subtitle: null,
  submitLabel: 'Continue',
  isSubmitVisible: false,
  isSubmitDisabled: true,
  submitButtonAppearance: 'primary',
  submitButtonAdditionalCssClassName: undefined,
  bottomChildren: undefined,
  footerLeftChildren: undefined,
};

export default StepContainer;
